import styled, { css } from 'styled-components';
import Button from 'v3/components/Button';
import { Text } from 'v3/components/OfferBox/styles';
import { Wrapper as CopyCode } from 'v3/components/CopyCode/styles';
import {
  ctaButtonOverrideCss,
  darkCheckboxCss,
  darkFieldsCss,
} from 'v3/components/FlowScreen/surfaces';

/* Shared chip appearance for single-choice controls on this screen. */
const chipCss = css<{ $selected?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  border-radius: 8px;
  padding: 0 16px;
  font-family: 'Inter', sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    color 0.15s ease;
  border: 1.5px solid
    ${({ $selected }) => ($selected ? '#5968db' : 'rgba(255, 255, 255, 0.14)')};
  background: ${({ $selected }) =>
    $selected ? 'rgba(89, 104, 219, 0.18)' : '#2b2a3a'};
  color: ${({ $selected }) =>
    $selected ? '#ffffff' : 'rgba(255, 255, 255, 0.72)'};

  &:hover {
    border-color: ${({ $selected }) =>
      $selected ? '#5968db' : 'rgba(255, 255, 255, 0.28)'};
  }
`;

type FieldsWrapperProps = {
  gap?: number;
  marginTop?: number;
};


export const TitleSection = styled.section`
  margin-bottom: 40px;
`;

export const Title = styled.h2`
  ${({ theme }) => css`
    font-size: 3.2rem;
    line-height: 3.8rem;
    font-weight: 600;
    color: ${theme.colors.neutralSilver80};
    margin-bottom: 40px;

    @media (max-width: 992px) {
      font-size: 2.4rem;
      line-height: 1.23;
    }
  `}
`;

export const Description = styled.p`
  ${({ theme }) => css`
    font-size: 1.6rem;
    line-height: 2.6rem;
    font-weight: 400;
    color: ${theme.colors.neutralSilver90};
    margin-bottom: 24px;
  `}
`;

/* Bordered, full-width pill. This used to be a rule at the top with the title
   loose underneath, which read as a section divider rather than a control. */
export const CollapseContainer = styled.div`
  order: 1;
  margin-top: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.04);
  transition: border-color 0.15s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.28);
  }

  .ant-collapse .ant-collapse-item .ant-collapse-header {
    padding: 16px 20px;
    font-family: 'Inter', sans-serif;
    font-size: 1.6rem;
    font-weight: 600;
    color: #ffffff;
  }

  .ant-collapse-content > .ant-collapse-content-box {
    padding: 0 20px 20px;
  }

  /* Collapse sets the header text colour itself, at a deeper selector than the
     header rule above. */
  .ant-collapse-header-text {
    color: #ffffff;
  }

  /* Collapse's secondary variant paints the header and the content box white; the
     page-level rule only reached the panel around them. */
  .ant-collapse-item .ant-collapse-header,
  .ant-collapse-content > .ant-collapse-content-box {
    background-color: transparent !important;
  }

  .ant-collapse-arrow svg {
    fill: rgba(255, 255, 255, 0.72);
  }

`;

export const ButtonSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;

  > button {
    min-width: 272px;

    display: flex;
    align-items: center;
    justify-content: center;

    /* The flow's one CTA treatment, shared with steps 0 and 2. This block used to
       hand-copy a purple that existed in two other files as well. */
    ${ctaButtonOverrideCss}
  }

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;

    > button {
      display: flex;
      justify-content: center;
      width: 100%;
    }
  }
`;

export const OfferBox = styled.div`
  ${Text} {
    @media (min-width: 768px) {
      font-size: 1.6rem;
      line-height: 2.6rem;
    }
  }
`;

export const FieldsWrapper = styled.div<FieldsWrapperProps>`
  ${({ gap = 16, marginTop = 0 }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${gap}px;
    margin-top: ${marginTop}px;
    width: 100%;

    > div {
      flex: 1;
    }

    label {
      font-size: 1.6rem;
    }

    @media (max-width: 450px) {
      flex-wrap: wrap;

      > div {
        flex: auto;
        width: 100%;
      }
    }
  `}
`;

export const TermWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  @media (max-width: 400px) {
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-start;
  }
`;

export const RadioLabel = styled.p`
  ${() => css`
    font-weight: 500;
    font-size: 1.6rem;
    line-height: 2.6rem;
    color: #e5e5e5;

    margin: 20px 0;
  `}
`;

/* Solid primary in the brand purple, full width. It was on the secondary
   variant, which left it washed out inside the panel. */
export const RecalculateButton = styled(Button)`
  width: 100%;
  justify-content: center;
  margin-top: 28px;
  background: #5968db !important;
  border-color: #5968db !important;
  color: #ffffff !important;
  border-radius: 8px !important;

  &:hover:not(:disabled) {
    background: #4a58c4 !important;
    border-color: #4a58c4 !important;
  }

  &:disabled {
    opacity: 0.45;
  }
`;

export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

export const SubscribeSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 42px;

  > button {
    min-width: 170px;
    display: flex;
    justify-content: center;
  }
`;

export const SubscribeIco = styled.img`
  margin-bottom: 16px;
`;

export const SubscribeTitle = styled.h3`
  ${({ theme }) => css`
    font-size: 2.4rem;
    font-weight: 600;
    color: ${theme.colors.neutralSilver80};
    margin-bottom: 8px;
  `}
`;

export const SubscribeDescription = styled.p`
  ${({ theme }) => css`
    font-size: 1.8rem;
    font-weight: 600;
    color: ${theme.colors.neutralSilver70};
    margin-bottom: 16px;

    > span {
      color: ${theme.colors.brandSecondary70};
    }
  `}
`;

export const SubscribeText = styled.p`
  ${({ theme }) => css`
    font-size: 1.4rem;
    font-weight: 600;
    color: ${theme.colors.neutralSilver60};
    margin: 16px 0 12px 0;
  `}
`;

export const InviteSection = styled.div`
  margin: 0 0 24px 0;
`;

export const InviteDescription = styled.p`
  ${({ theme }) => css`
    font-size: 1.6rem;
    font-weight: 500;
    color: ${theme.colors.neutralSilver60};
    margin-bottom: 24px;
  `}
`;

export const EmailsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;

  > button {
    margin-top: 8px;
    min-width: 170px;
    display: flex;
    justify-content: center;
  }
`;

export const EmailsLabel = styled.div`
  width: 100%;
`;

export const EmailTitle = styled.h4`
  ${({ theme }) => css`
    font-size: 1.4rem;
    font-weight: 500;
    color: ${theme.colors.neutralSilver70};
    line-height: 22px;
    margin: 0;
  `}
`;

export const EmailDescription = styled.p`
  ${({ theme }) => css`
    font-size: 1.2rem;
    color: ${theme.colors.neutralSilver50};
    margin-bottom: 8px;
  `}
`;

export const ReferralSection = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.neutralSilver5};
    padding: 24px;
    width: 100%;
    border-radius: 8px;
    display: flex;
    gap: 16px;
    justify-content: space-between;

    ${CopyCode} {
      width: 280px;
    }

    @media (max-width: 576px) {
      flex-direction: column;

      ${CopyCode} {
        width: 100%;
      }
    }
  `}
`;

export const ReferralContent = styled.div``;

export const ReferralText = styled.p`
  ${({ theme }) => css`
    font-size: 1.4rem;
    color: ${theme.colors.neutralSilver60};
    line-height: 1.6;
    margin: 0;

    > strong {
      font-weight: 700;
      color: ${theme.colors.brandPrimary70};
    }
  `}
`;

export const SubscribeContent = styled.div`
  ${({ theme }) => css`
    z-index: 2;
    position: relative;
    background-color: ${theme.colors.globalWhite100};
    padding: 42px 0 0 0;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: -24px;
      width: 24px;
      height: 100%;
      z-index: 1;
      background-color: ${theme.colors.globalWhite100};
    }

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      right: -24px;
      width: 24px;
      height: 100%;
      z-index: 1;
      background-color: ${theme.colors.globalWhite100};
    }

    @media (max-width: 992px) {
      padding: 24px 0 48px 0;
      margin-top: 32px;
      margin-bottom: -80px;
    }
  `}
`;

/* Adjust region: a strong rule separates the answer (the numbers) from the
   controls that change it. Inside it the field labels are subordinate to the
   group title, rather than four siblings of equal weight. */
/* Footer as its own block, with breathing room around it. The consent and the
   button used to butt against the collapse above and against each other.
   It lives inside the card: outside it the pair floated on the grey background
   and read as unfinished. No rule above it — the consent is about the video
   immediately above, so a line between them would break the very relationship
   that gives it meaning. */
export const Footer = styled.div`
  margin-top: 20px;

  /* The design-system Checkbox carries no margin of its own; spacing is set here. */
  > form > label {
    display: flex;
    margin-bottom: 12px;
  }

  button[type='submit'] {
    border-radius: 8px !important;
  }
`;

/* The rule goes here, not between the video and the consent: up to "Want to make
   a change?" the screen is about changing the offer; from the video on it is about
   understanding and moving forward. Video and checkbox are one conversation. */
export const VideoSlot = styled.div`
  margin-top: 22px;
  padding-top: 22px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

/* Controls above the summary — today just the frequency, which changes the headline. */
export const Adjust = styled.div`
  margin-bottom: 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

/* Controls below the summary. One rhythm between the fields, rather than each
   block bringing its own spacing. */
export const AfterOffer = styled.div`
  order: 2;
  margin-top: 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;


/* Payment frequency. These were bare, unlabelled radios. They now use the chip
   appearance but remain real radios: the group is announced as "1 of 2" and
   responds to arrow keys, which a set of aria-pressed buttons would not. */
export const CycleFieldset = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0 0 20px;
`;

export const CycleLegend = styled.legend`
  padding: 0;
  margin-bottom: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  color: #e5e5e5;
`;

export const CycleWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Chip = styled.label<{ $selected?: boolean }>`
  position: relative;
  ${chipCss}

  /* The input stays in flow and focusable — just invisible. display:none would
     drop it from the tab order and kill the group's arrow-key navigation. */
  input {
    position: absolute;
    inset: 0;
    opacity: 0;
    margin: 0;
    cursor: pointer;
  }

  &:focus-within {
    outline: none;
    border-color: #5968db;
    box-shadow: 0 0 0 3px rgba(89, 104, 219, 0.4);
  }
`;

export const LoadingWrapper = styled.div`
  padding: 72px 0;
`;

export const LoadingContent = styled.p`
  text-align: center;
`;

// --- Light DS "offer" reskin (portado do design do site) ---

export const InfoPane = styled.div`
  font-family: 'Inter', sans-serif;
  /* 768 = a 720 card plus 24px of padding each side, so the card box matches
     Getting Started's exactly. It was 576, which made the flow narrow between
     step 1 and step 2. */
  max-width: 768px;
  width: 100%;
  margin: 0 auto;
  padding: 32px 24px;

  @media (min-width: 768px) {
    padding: 40px 24px;
  }
`;

export const Eyebrow = styled.p`
  font-size: 1.4rem;
  font-weight: 600;
  line-height: 1.5;
  margin: 0 0 16px 0;

  .phase {
    color: #b3abe8;
  }

  .rest {
    color: rgba(255, 255, 255, 0.55);
  }
`;

/* Site scale (heading-xl: 2.15rem on a 16px base = 34px, weight 600, tracking
   -0.018em). The base here is 10px, hence 3.4rem. */
export const InfoTitle = styled.h1`
  font-size: 3.4rem;
  font-weight: 600;
  line-height: 1.18;
  letter-spacing: -0.018em;
  color: #ffffff;
  /* 24px down to the first field. It was 8px, inherited from when a subtitle
     sat directly below. */
  margin: 0 0 24px 0;

  @media (max-width: 576px) {
    font-size: 2.8rem;
  }
`;

export const InfoSubtitle = styled.p`
  font-size: 1.6rem;
  font-weight: 400;
  line-height: 1.5;
  color: #bebebe;
  margin: 0 0 24px 0;
`;

export const CardShell = styled.div`
  /* The card matches Getting Started's: translucent white on the dark screen, so
     the three steps read as one product rather than two.

     Everything below themes shared components by descent. OfferBox, Collapse,
     Checkbox and the design-system inputs are light by definition and are used by
     ReviewOfferWaitlist, ChangePaymentMethod and the v2 app, so none of them is
     modified — the dark treatment is scoped to this card. */
  > section {
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    padding: 28px 32px;
    color: #e5e5e5;

    @media (max-width: 576px) {
      padding: 24px 20px;
    }
  }

  ${darkFieldsCss}
  ${darkCheckboxCss}

  ${OfferBox},
  ${OfferBox} > div {
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    padding: 0;
  }

  /* Visual order differs from JSX order: "Want to make a change?" has to sit
     directly below the summary, because "a change" refers to those numbers — with
     the referral in between, the pronoun loses its antecedent. Done with CSS order
     so the collapse block, which carries the recalculate Formik, doesn't move. */
  ${OfferBox} > div {
    display: flex;
    flex-direction: column;
  }

  /* The summary is the money moment, set back into the card. On the light screen
     that was a grey inset; here it is a deeper well. */
  ${OfferBox} > div > div:first-child {
    order: 0;
    border: none;
    border-radius: 24px;
    background: rgba(0, 0, 0, 0.22);
    box-shadow: none;
    padding: 24px;

    @media (max-width: 576px) {
      padding: 20px;
      border-radius: 16px;
    }
  }

  /* OfferBox writes its labels and figures in light-theme ink. */
  ${Text} {
    color: rgba(255, 255, 255, 0.72) !important;
  }

  ${OfferBox} p[aria-label],
  ${OfferBox} strong {
    color: #ffffff !important;
  }

  /* antd brings its own treatment (#fafafa, radius 2), which was a fourth
     container system on one screen. */
  .ant-collapse,
  .ant-collapse > .ant-collapse-item,
  .ant-collapse-content {
    background: transparent !important;
    border-radius: 0 !important;
    border: none !important;
  }

  .ant-collapse,
  .ant-collapse-header,
  .ant-collapse-content {
    color: #e5e5e5 !important;
  }

  .ant-collapse-arrow svg {
    fill: rgba(255, 255, 255, 0.72);
  }

  /* The flow's CTA purple, flat rather than the shared variant's gradient. */
  button[type='submit'] {
    background: #5968db !important;
    border-color: #5968db !important;
  }

  button[type='submit']:hover:not(:disabled) {
    background: #4a58c4 !important;
    border-color: #4a58c4 !important;
  }

  input:checked ~ span {
    background-color: #5968db !important;
    border-color: #5968db !important;
  }
`;
